<script>
	import { closeModal, modals } from 'svelte-modals';
	import { createEventDispatcher, onDestroy, onMount } from 'svelte';
	import { AnalyticsService } from '$lib/services/analytics';
	import { app } from '$lib/stores/app/store';
	import { registerEmail } from '$lib/stores/user/action';
	import { AOFL_PRODUCT_NAMES, EVENT_NAMES, UsernameTypes } from '$lib/utils/constants';
	import { startLoading } from '$lib/stores/app/actions';
	import ProspectRegisterCard from './ProspectRegisterCard.svelte';
	import getPageConfig from '../__config/page-configuration';

	let { prospectRegisterContent } = getPageConfig($app);
	const isProspectRegisterModal = true;

	export let videoSrc = '';

	const dispatch = createEventDispatcher();

	const HOME_VIDEO_MODAL_STATE = {
		VIDEO: 'VIDEO',
		MINI_REG: 'MINI_REG'
	};

	let modalState = HOME_VIDEO_MODAL_STATE.VIDEO;

	/** @type {HTMLVideoElement} */
	let homeVideo;

	/**
	 * Pauses the video, logs the completion event, and resets the video to the beginning.
	 * @async
	 * @returns {Promise<void>}
	 */
	async function pauseVideo() {
		if (homeVideo && !homeVideo.paused) {
			homeVideo.pause();
			await logVideoCompletionEvent(homeVideo, homeVideo.currentTime);
			if (homeVideo) {
				// Checking for homeVideo to be defined in case DOM state changes before logVideoCompletionEvent completes
				homeVideo.currentTime = 0;
			}
		}
	}

	/**
	 * Logs the video completion event
	 * @param {HTMLVideoElement} video - The video element
	 * @param {number} videoCurrentTime - The current time of the video
	 */
	function logVideoCompletionEvent(video, videoCurrentTime) {
		const pageName = AnalyticsService.createPageName();
		let videoCompletion = Number((videoCurrentTime / video.duration) * 100);
		videoCompletion =
			Number.isNaN(videoCompletion) || !isFinite(videoCompletion) ? 0 : videoCompletion;
		videoCompletion = Math.round(videoCompletion);
		const videoDuration = Math.round(video.duration);

		try {
			AnalyticsService.fireEvent({
				event: {
					event_type: EVENT_NAMES.VIDEO_PROGRESS,
					event_payload: {
						aofl_product: AOFL_PRODUCT_NAMES.ABCMOUSE,
						video_info: {
							page_name: pageName,
							percent_watched: videoCompletion,
							site_section: 'regpath',
							video_length_seconds: videoDuration,
							video_name: videoSrc,
							video_watched_seconds: videoCurrentTime
						}
					}
				}
			});
		} catch (e) {}
	}

	/**
	 * @param {{ detail: { email: string; }; }} event
	 */
	async function onProspectSubmit(event) {
		const { email } = event?.detail;
		if (email) {
			await registerEmail(
				{
					email,
					countryAbbrv: $app.meta.viewerCountry,
					usernameType: UsernameTypes.EMAIL
				},
				{
					redirect: true
				}
			);
			closeModal();
		}
	}

	/**
	 * Closes the modal if it's in the MINI_REG state
	 */
	function closeModalHomeVideo() {
		if (modalState === HOME_VIDEO_MODAL_STATE.MINI_REG) {
			closeModal();
		}
	}

	/**
	 * Handles the modal close action
	 */
	function onModalClose() {
		pauseVideo();
		modalState = HOME_VIDEO_MODAL_STATE.MINI_REG;
	}

	/**
	 * Event handler for keyboard events.
	 *
	 * @param {KeyboardEvent} evt - The keyboard event object.
	 * @returns {void}
	 */
	const evtHandler = (evt) => {
		dispatch('keydown', evt);
		if (evt.key === 'Escape') {
			if (modalState === HOME_VIDEO_MODAL_STATE.VIDEO) {
				onModalClose();
			} else {
				closeModal();
			}
		}
		return;
	};

	onMount(() => {
		if (homeVideo) {
			homeVideo.play();
		}

		window.addEventListener('global:backdrop-click', closeModalHomeVideo);

		// TODO: common utitlity to handle this.
		if (typeof document !== 'undefined') {
			modals.subscribe((modals) => {
				if (modals.length > 0) {
					document.documentElement.style.setProperty('overflow-y', 'hidden');
				} else {
					document.documentElement.style.setProperty('overflow-y', 'visible');
				}
			});
		}
	});

	onDestroy(() => {
		pauseVideo();
		modalState = HOME_VIDEO_MODAL_STATE.VIDEO;
	});
</script>

<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
<aside on:keydown={evtHandler} role="dialog" class="modal-base group w-full md:w-[80rem]">
	{#if modalState === HOME_VIDEO_MODAL_STATE.VIDEO}
		<div
			id="modal-close-button"
			class="absolute right-4 top-4 z-[1] flex hidden h-12 w-12 cursor-pointer items-center justify-center rounded-md bg-black bg-opacity-65 group-hover:block"
			on:click={onModalClose}
			on:keydown={onModalClose}
			role="button"
			tabindex="0"
		>
			<span
				class="absolute bottom-[0.3rem] left-[1.4rem] block h-6 w-[0.2rem] rotate-45 transform bg-white"
			></span>
			<span
				class="absolute bottom-[0.3rem] left-[1.4rem] block h-6 w-[0.2rem] -rotate-45 transform bg-white"
			></span>
		</div>
		<video
			bind:this={homeVideo}
			id="home-video"
			class="h-auto w-full"
			src={videoSrc}
			controls
			controlsList="nodownload"
			poster="noposter"
			on:ended={onModalClose}
		></video>
	{:else if modalState === HOME_VIDEO_MODAL_STATE.MINI_REG}
		<ProspectRegisterCard
			on:submit={onProspectSubmit}
			content={prospectRegisterContent}
			{isProspectRegisterModal}
		/>
	{/if}
</aside>
